import { Link } from 'react-router-dom';
import { CalendarClock, ArrowRight, BookOpen, Clock, CheckCircle2 } from 'lucide-react';
import Card from '../ui/Card';
import type { Week } from '../../services/coursesApi';
import {
  firstLessonId,
  formatDuration,
  sessionChipClass,
  sessionShortLabel,
  weekCtaLabel,
  weekEstimatedMinutes,
  weekGradient,
  weekHasLessons,
  weekLessonCount,
  weekLessonProgress,
  weekImageUrl,
} from './weekPresentation';

// A single week tile: banner (image or themed gradient), WEEK N eyebrow, title,
// meta row (lessons + est. time), then either a lesson-progress bar with a clear
// CTA (lecture weeks) or the session plan (review/test/reading weeks). Links into
// the first lesson when one exists.
export default function WeekCard({ week }: { week: Week }) {
  const lessonId = firstLessonId(week);
  const { completed, total } = weekLessonProgress(week);
  const hasLessons = weekHasLessons(week);
  const sessions = week.schedule?.sessions ?? [];
  const due = week.schedule?.due ?? [];
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const lessonCount = weekLessonCount(week);
  const estMinutes = weekEstimatedMinutes(week);
  const isComplete = total > 0 && completed >= total;
  const bannerUrl = weekImageUrl(week);

  const inner = (
    <Card interactive={!!lessonId} className="flex h-full flex-col overflow-hidden">
      {/* Banner */}
      <div
        className="relative aspect-[21/9] w-full overflow-hidden"
        style={
          bannerUrl
            ? { backgroundImage: `url(${bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { backgroundImage: weekGradient(week.index) }
        }
      >
        {/* Legibility scrim for the overlaid eyebrow/badges */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/10" />
        <span className="absolute left-3 top-3 rounded-md bg-black/35 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
          Week {week.index}
        </span>
        {isComplete && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-ws-success shadow-sm">
            <CheckCircle2 size={12} /> Done
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <h3 className="text-sm font-semibold leading-snug text-ws-ink line-clamp-2">
          {week.title}
        </h3>

        {/* Meta row — lessons + est. time */}
        {hasLessons && lessonCount > 0 && (
          <div className="flex items-center gap-3 text-[11px] text-ws-ink-3">
            <span className="inline-flex items-center gap-1">
              <BookOpen size={12} />
              {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
            </span>
            {estMinutes > 0 && (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                {formatDuration(estMinutes)}
              </span>
            )}
          </div>
        )}

        {/* Lecture weeks → progress + CTA */}
        {hasLessons && total > 0 && (
          <div>
            <div className="flex items-center justify-between text-[11px] text-ws-ink-3">
              <span>
                {completed}/{total} completed
              </span>
              <span className="tabular-nums font-medium text-ws-ink-2">{pct}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ws-surface-2">
              <div
                className={`ws-progress-fill h-full rounded-full ${
                  isComplete ? 'bg-ws-success' : 'bg-ws-accent'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* Session plan chips */}
        {sessions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {sessions.map((s, i) => (
              <span
                key={i}
                title={s.label}
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${sessionChipClass(s.type)}`}
              >
                {sessionShortLabel(s)}
              </span>
            ))}
          </div>
        )}

        {/* Due items */}
        {due.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 border-t border-ws-border pt-1.5">
            <CalendarClock size={12} className="text-ws-warning" />
            {due.map((d, i) => (
              <span
                key={i}
                title={d}
                className="rounded border border-ws-border px-1.5 py-0.5 text-[10px] font-medium text-ws-warning"
              >
                {d}
              </span>
            ))}
          </div>
        )}

        {/* CTA — pinned to the bottom for lecture weeks */}
        {lessonId && (
          <div className="mt-auto">
            <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ws-accent transition-colors group-hover/card:text-ws-accent-hover">
              {weekCtaLabel(completed, total)}
              <ArrowRight size={13} className="transition-transform duration-200 group-hover/card:translate-x-0.5" />
            </span>
          </div>
        )}
      </div>
    </Card>
  );

  // Lecture weeks link to their first lesson; plan-only weeks are static cards.
  return lessonId ? (
    <Link to={`/lessons/${lessonId}`} className="group/card block h-full">
      {inner}
    </Link>
  ) : (
    <div className="h-full">{inner}</div>
  );
}
